"use client";

import { Button } from "@/components/ui/button";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AnimatedArrow } from "./animated-arrow";
import { ContactSchema } from "@/types/contact";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid work email" }),
}) satisfies z.ZodType<ContactSchema>;

const BookDemo = () => {
  const router = useRouter();
  const posthog = usePostHog();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const sendContact = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: data.email,
          first_name: "",
          company: "",
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to send contact: ${res.statusText}`);
      }

      return res.json();
    },
  });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setError(null);
    sendContact.mutate(data, {
      onSuccess: () => {
        posthog.capture("contact_form_submitted", {
          email: data.email,
        });
        router.push(`/contact?email=${encodeURIComponent(data.email)}`);
      },
      onError: (error) => {
        setError("Failed to submit. Please try again later.");
        console.error(error);
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="What's your work email?"
                  {...field}
                  className="w-[350px] rounded-xl p-1"
                >
                  <Button
                    type="submit"
                    variant="default"
                    className="group flex flex-shrink-0 items-center gap-1.5"
                    disabled={sendContact.isPending}
                    isLoading={sendContact.isPending}
                  >
                    Get Started
                    <AnimatedArrow />
                  </Button>
                </Input>
              </FormControl>
              <FormMessage />
              {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default BookDemo;
